import { useNavigate } from "react-router";
import type { ReactElement } from "react";
import { Navbar } from "../../components/Navbar/Navbar";
import { useLayout } from "../../utils/hooks/useLayout";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import { navigateToNewState } from "../../utils/location/navigateToNewState";
import {
  CurrencyScreenContent,
  useCurrencyPreferenceScreen,
} from "../CurrencyScreen/CurrencyScreen";
import {
  DistanceScreenContent,
  useDistancePreferenceScreen,
} from "../DistanceScreen/DistanceScreen";
import {
  LanguageScreenContent,
  useLanguagePreferenceScreen,
} from "../LanguageScreen/LanguageScreen";
import {
  TimeFormatScreenContent,
  useTimeFormatPreferenceScreen,
} from "../TimeFormatScreen/TimeFormatScreen";

type Preference = "distance" | "language" | "currency" | "timeFormat";
type Props = {
  preference: Preference;
};

const screenContents: Record<Preference, () => ReactElement> = {
  distance: DistanceScreenContent,
  language: LanguageScreenContent,
  currency: CurrencyScreenContent,
  timeFormat: TimeFormatScreenContent,
};

export function PreferenceScreen(props: Props) {
  const navigate = useNavigate();
  const location = useTypedLocation();
  const layout = useLayout();

  const InnerContent = screenContents[props.preference];

  const { heading: distanceHeading, description: distanceDescription } =
    useDistancePreferenceScreen();
  const { heading: languageHeading, description: languageDescription } =
    useLanguagePreferenceScreen();
  const { heading: currencyHeading, description: currencyDescription } =
    useCurrencyPreferenceScreen();
  const { heading: timeFormatHeading, description: timeFormatDescription } =
    useTimeFormatPreferenceScreen();

  const screenTexts: Record<
    Preference,
    { heading: string; description?: string }
  > = {
    distance: {
      heading: distanceHeading,
      description: distanceDescription,
    },
    language: {
      heading: languageHeading,
      description: languageDescription,
    },
    currency: {
      heading: currencyHeading,
      description: currencyDescription,
    },
    timeFormat: {
      heading: timeFormatHeading,
      description: timeFormatDescription,
    },
  };

  const { heading, description } = screenTexts[props.preference];

  function handleBackClick() {
    // go back to main user preferences screen
    navigateToNewState(navigate, { preferencesScreen: "main" }, location);
  }

  return (
    <>
      {layout === "mobile" || layout === "tablet" ? (
        <Navbar headingText={heading} onBackClick={handleBackClick} />
      ) : (
        description && (
          <h2 className="my-2 ml-0 text-heading-lg lg:mt-4">{description}</h2>
        )
      )}
      <div className="max-h-9/10 overflow-y-auto px-6 pt-6 pb-4 lg:mt-3 lg:px-0 lg:pt-0">
        <InnerContent />
      </div>
    </>
  );
}
